// ════════════════════════════════════════════════════════════════════════════
// 🚫 VoidModal — Phase 1.3 Void/Refund flow with Manager session gate
// ════════════════════════════════════════════════════════════════════════════
// Calls POST /api/transactions/:txRef/void
// Atomic on the backend: restores stock + sets status='voided' + reverses loyalty.
// 🛡️ Phase 5: Uses Bearer JWT (Phase 4 RBAC) — requires active manager session.
// Modal itself is the friction-introducing confirmation per UX research §Error Correction.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { AlertTriangle, X, Lock, RotateCcw, ShieldAlert } from 'lucide-react';

interface VoidModalProps {
  open: boolean;
  txRef: string | null;
  txTotal: number;
  storeApiKey: string;
  onClose: () => void;
  onVoided: (txRef: string) => void;
}

const formatRs = (n: number) =>
  'Rs. ' + n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const VoidModal: React.FC<VoidModalProps> = ({ open, txRef, txTotal, storeApiKey, onClose, onVoided }) => {
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setPin(''); setReason(''); setError(null); setSubmitting(false); }
  }, [open]);

  if (!open || !txRef) return null;

  const submit = async () => {
    if (!reason.trim()) { setError('Reason required for audit trail'); return; }
    // 🛡️ Phase 5 — RBAC: pull Bearer JWT from cashier session
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) {
      setError('Manager sign-in required. Please sign in from the header badge.');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`/api/transactions/${encodeURIComponent(txRef)}/void`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
          'Authorization': `Bearer ${token}`,
        },
        // managerPin is kept for backend backward-compat if present; reason is the required audit field.
        body: JSON.stringify({ managerPin: pin || undefined, reason: reason.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) {
          setError(data?.error || 'Only managers can void transactions. Sign in as a manager first.');
        } else {
          setError(data?.error || `Void failed (HTTP ${res.status})`);
        }
        return;
      }
      onVoided(txRef);
    } catch {
      setError('Cannot reach server');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="void-bg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10001] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      >
        <motion.div
          key="void-card"
          initial={{ scale: 0.95, y: 12, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 12, opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start gap-3 px-5 pt-5 pb-3 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center shrink-0">
              <AlertTriangle size={18} className="text-rose-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[14px] font-black text-slate-800">Void Transaction</h3>
              <p className="text-[11px] text-slate-500 mt-0.5 font-mono">{txRef}</p>
              <p className="text-[12px] text-rose-600 font-bold mt-1.5">{formatRs(txTotal)} will be reversed</p>
            </div>
            <button
              onClick={() => !submitting && onClose()}
              className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors shrink-0"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 space-y-3">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex gap-2">
              <RotateCcw size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-[11px] text-amber-800 leading-snug">
                Stock will be restored, transaction marked <strong>voided</strong>. This action is logged but cannot be undone.
              </div>
            </div>

            {/* 🛡️ Phase 5 — Manager session indicator. Authority is verified server-side via Bearer JWT. */}
            <div className="rounded-xl bg-violet-50 border border-violet-200 p-3 flex gap-2">
              <ShieldAlert size={14} className="text-violet-600 shrink-0 mt-0.5" />
              <div className="text-[11px] text-violet-800 leading-snug">
                Your <strong>manager sign-in</strong> authorizes this void. Make sure you are the person taking responsibility for this reversal.
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Lock size={10} /> Extra PIN <span className="text-slate-400 normal-case font-normal">(optional, for double-check)</span>
              </label>
              <input
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Skip if not required"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[14px] font-mono tracking-widest text-center"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Reason (audit trail)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Customer returned item / Wrong product scanned / etc."
                rows={2}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[12px] resize-none"
              />
            </div>

            {error && (
              <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-2 px-5 pb-5">
            <button
              onClick={() => !submitting && onClose()}
              disabled={submitting}
              className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-[12px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || !reason.trim()}
              className="flex-1 py-2.5 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-[12px] font-bold uppercase tracking-wider shadow-md shadow-rose-500/20 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97] transition-all"
            >
              {submitting ? 'Voiding…' : 'Void Transaction'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default VoidModal;
